import { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';

import { get_invoice_settings, update_invoice_settings } from '../api/dev';
import {
    createInvoiceAccountCode,
    fetchInvoiceAccountCodes,
    updateInvoiceAccountCode,
} from '../api/invoices';
import { ActivityIndicator } from './custom/ActivityIndicator';
import './css/InvoiceSettingsPanel.css';


const emptySettings = {
    company_name: '',
    address_line_1: '',
    address_line_2: '',
    town_city: '',
    county: '',
    postcode: '',
    company_number: '',
    vat_number: '',
    contact_email: '',
    contact_phone: '',
    default_vat_rate: 20,
    payment_terms_days: 30,
    bank_name: '',
    bank_account_name: '',
    bank_sort_code: '',
    bank_account_number: '',
    iban: '',
    bic: '',
    payment_instructions: '',
};

const fields = {
    company: [
        ['company_name', 'Company name', true],
        ['company_number', 'Company registration number'],
        ['address_line_1', 'Address line 1'],
        ['address_line_2', 'Address line 2'],
        ['town_city', 'Town / city'],
        ['county', 'County'],
        ['postcode', 'Postcode'],
    ],
    taxAndContact: [
        ['vat_number', 'VAT registration number'],
        ['default_vat_rate', 'Default VAT rate (%)', true, 'number'],
        ['payment_terms_days', 'Default payment terms (days)', true, 'number'],
        ['contact_email', 'Accounts email', false, 'email'],
        ['contact_phone', 'Accounts telephone'],
    ],
    bank: [
        ['bank_account_name', 'Account name'],
        ['bank_name', 'Bank name'],
        ['bank_sort_code', 'Sort code'],
        ['bank_account_number', 'Account number'],
        ['iban', 'IBAN'],
        ['bic', 'BIC / SWIFT'],
    ],
};

const InvoiceSettingsPanel = ({ showAlert }) => {
    const [settings, setSettings] = useState(emptySettings);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [accountCodes, setAccountCodes] = useState([]);
    const [newAccountCode, setNewAccountCode] = useState({ code: '', name: '', is_pallet_network: false });
    const [savingAccountCodeId, setSavingAccountCodeId] = useState(null);

    const loadSettings = useCallback(async () => {
        setIsLoading(true);
        const [settingsResponse, codesResponse] = await Promise.all([
            get_invoice_settings(),
            fetchInvoiceAccountCodes(true),
        ]);
        if (settingsResponse.status === 200) {
            setSettings({ ...emptySettings, ...settingsResponse.data });
        } else {
            showAlert({ message: settingsResponse.data?.error || 'Unable to load invoice settings.', type: 'danger', buttons: [{ label: 'Close' }] });
        }
        if (codesResponse.status === 200) {
            setAccountCodes(codesResponse.data.account_codes || []);
        } else {
            showAlert({ message: codesResponse.data?.error || 'Unable to load invoice account codes.', type: 'danger', buttons: [{ label: 'Close' }] });
        }
        setIsLoading(false);
    }, [showAlert]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const updateField = (key, value) => {
        setSettings(current => ({ ...current, [key]: value }));
    };

    const saveSettings = async () => {
        if (!settings.company_name.trim()) {
            showAlert({ message: 'Company name is required.', type: 'warning', buttons: [{ label: 'Close' }] });
            return;
        }
        setIsSaving(true);
        const response = await update_invoice_settings(settings);
        if (response.status === 200) {
            setSettings({ ...emptySettings, ...response.data });
            showAlert({ message: 'Invoice settings updated.', type: 'success', buttons: [{ label: 'Close' }] });
        } else {
            showAlert({ message: response.data?.error || 'Unable to update invoice settings.', type: 'danger', buttons: [{ label: 'Close' }] });
        }
        setIsSaving(false);
    };

    const addAccountCode = async () => {
        if (!newAccountCode.code.trim() || !newAccountCode.name.trim()) {
            showAlert({ message: 'Account code and description are required.', type: 'warning', buttons: [{ label: 'Close' }] });
            return;
        }
        setSavingAccountCodeId('new');
        const response = await createInvoiceAccountCode(newAccountCode);
        setSavingAccountCodeId(null);
        if (response.status === 201) {
            setNewAccountCode({ code: '', name: '', is_pallet_network: false });
            await loadSettings();
        } else {
            showAlert({ message: response.data?.error || 'Unable to create account code.', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const updateAccountCodeField = (id, field, value) => {
        setAccountCodes(current => current.map(accountCode =>
            accountCode.id === id ? { ...accountCode, [field]: value } : accountCode
        ));
    };

    const saveAccountCode = async (accountCode) => {
        setSavingAccountCodeId(accountCode.id);
        const response = await updateInvoiceAccountCode(accountCode.id, {
            code: accountCode.code,
            name: accountCode.name,
            is_active: accountCode.is_active,
            is_pallet_network: accountCode.is_pallet_network,
        });
        setSavingAccountCodeId(null);
        if (response.status === 200) {
            await loadSettings();
        } else {
            showAlert({ message: response.data?.error || 'Unable to update account code.', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const renderFields = (fieldList) => fieldList.map(([key, label, required, type = 'text']) => (
        <Col md={6} key={key}>
            <Form.Group className="mb-3">
                <Form.Label><strong>{label}{required ? ' *' : ''}</strong></Form.Label>
                <Form.Control
                    type={type}
                    min={type === 'number' ? 0 : undefined}
                    max={key === 'default_vat_rate' ? 100 : key === 'payment_terms_days' ? 365 : undefined}
                    step={key === 'default_vat_rate' ? '0.01' : undefined}
                    value={settings[key] ?? ''}
                    onChange={event => updateField(key, event.target.value)}
                    required={required}
                />
            </Form.Group>
        </Col>
    ));

    if (isLoading) {
        return <div style={{ textAlign: 'center', padding: '40px' }}><ActivityIndicator /></div>;
    }

    return (
        <div className="invoice-settings-panel">
            <div className="invoice-settings-heading">
                <div>
                    <span className="invoice-settings-eyebrow">Invoicing</span>
                    <h3>Invoice settings</h3>
                    <p>Manage the details printed on invoices and the revenue codes used for accounting.</p>
                </div>
            </div>

            <Form className="invoice-settings-form" onSubmit={event => { event.preventDefault(); saveSettings(); }}>
                <div className="invoice-settings-grid">
                    <section className="invoice-settings-card">
                        <div className="invoice-settings-card-heading">
                            <h5>Company details</h5>
                            <p>Legal name and address shown at the top of every invoice.</p>
                        </div>
                        <Row>{renderFields(fields.company)}</Row>
                    </section>

                    <section className="invoice-settings-card">
                        <div className="invoice-settings-card-heading">
                            <h5>Tax, terms and contact</h5>
                            <p>Default VAT, payment terms and accounts contact information.</p>
                        </div>
                        <Row>{renderFields(fields.taxAndContact)}</Row>
                    </section>
                </div>

                <section className="invoice-settings-card">
                    <div className="invoice-settings-card-heading">
                        <h5>Bank and payment details</h5>
                        <p>Payment information printed on the invoice for customer remittances.</p>
                    </div>
                    <Row>{renderFields(fields.bank)}</Row>
                    <Form.Group className="mb-3">
                        <Form.Label><strong>Payment instructions</strong></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={settings.payment_instructions || ''}
                            onChange={event => updateField('payment_instructions', event.target.value)}
                            placeholder="Additional remittance or payment instructions"
                        />
                    </Form.Group>
                </section>

                <div className="invoice-settings-save-bar">
                    <div>
                        <strong>Invoice document details</strong>
                        <span>Changes apply to newly generated invoice documents.</span>
                    </div>
                    <Button type="submit" variant="primary" disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save invoice settings'}
                    </Button>
                </div>
            </Form>

            <section className="invoice-settings-card invoice-account-codes-card">
                <div className="invoice-settings-card-heading invoice-account-codes-heading">
                    <div>
                        <h5>Revenue account codes</h5>
                        <p>Every invoice requires a code. Xero receives it as the AccountCode for audit reporting.</p>
                    </div>
                    <span className="invoice-settings-count">{accountCodes.length} codes</span>
                </div>

                <div className="invoice-account-code-help">
                    Codes already used on invoices cannot be renamed, but they can be archived. Enable <strong>Pallet network</strong> only for codes that should create the landscape consignment invoice.
                </div>

                <div className="invoice-account-code-list">
                    {accountCodes.map(accountCode => (
                        <div
                            key={accountCode.id}
                            className={`invoice-account-code-row${accountCode.is_active ? '' : ' archived'}`}
                        >
                            <Form.Group>
                                <Form.Label>Code</Form.Label>
                                <Form.Control
                                    value={accountCode.code}
                                    disabled={accountCode.is_used}
                                    onChange={event => updateAccountCodeField(accountCode.id, 'code', event.target.value)}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>
                                    Description
                                    {accountCode.is_used && <span className="invoice-code-locked">Locked after use</span>}
                                </Form.Label>
                                <Form.Control
                                    value={accountCode.name}
                                    disabled={accountCode.is_used}
                                    onChange={event => updateAccountCodeField(accountCode.id, 'name', event.target.value)}
                                />
                            </Form.Group>
                            <div className="invoice-account-code-switch">
                                <Form.Check
                                    type="switch"
                                    label="Pallet network"
                                    checked={accountCode.is_pallet_network || false}
                                    onChange={event => updateAccountCodeField(accountCode.id, 'is_pallet_network', event.target.checked)}
                                />
                            </div>
                            <div className="invoice-account-code-switch">
                                <Form.Check
                                    type="switch"
                                    label={accountCode.is_active ? 'Active' : 'Archived'}
                                    checked={accountCode.is_active}
                                    onChange={event => updateAccountCodeField(accountCode.id, 'is_active', event.target.checked)}
                                />
                            </div>
                            <div className="invoice-account-code-action">
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    disabled={savingAccountCodeId === accountCode.id}
                                    onClick={() => saveAccountCode(accountCode)}
                                >
                                    {savingAccountCodeId === accountCode.id ? 'Saving...' : 'Save'}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="invoice-account-code-new">
                    <div className="invoice-account-code-new-heading">
                        <strong>Add revenue code</strong>
                        <span>Create a new code for an additional revenue stream.</span>
                    </div>
                    <div className="invoice-account-code-new-fields">
                        <Form.Group>
                            <Form.Label>Code</Form.Label>
                            <Form.Control
                                value={newAccountCode.code}
                                placeholder="e.g. 4050"
                                onChange={event => setNewAccountCode(current => ({ ...current, code: event.target.value }))}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                value={newAccountCode.name}
                                placeholder="Revenue stream description"
                                onChange={event => setNewAccountCode(current => ({ ...current, name: event.target.value }))}
                            />
                        </Form.Group>
                        <div className="invoice-account-code-switch">
                            <Form.Check
                                type="switch"
                                label="Pallet network"
                                checked={newAccountCode.is_pallet_network}
                                onChange={event => setNewAccountCode(current => ({ ...current, is_pallet_network: event.target.checked }))}
                            />
                        </div>
                        <div className="invoice-account-code-action">
                            <Button
                                type="button"
                                variant="success"
                                disabled={savingAccountCodeId === 'new'}
                                onClick={addAccountCode}
                            >
                                {savingAccountCodeId === 'new' ? 'Adding...' : 'Add code'}
                            </Button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default InvoiceSettingsPanel;